/**
 * UserQuickActions — dropdown menu di azioni rapide per riga utente.
 *
 * Azioni (admin SUPER-MVP M2):
 *  1. Apri detail              (link a /admin/users/:uid)
 *  2. Modifica dettagli        (drawer scivolante EditUserDrawer)
 *  3. Reset password           (ReasonConfirmDialog → genera link Firebase + invia email)
 *  4. Disable / Enable         (ReasonConfirmDialog toggle in base a disabled)
 *  5. Impersonate              (ReasonConfirmDialog → ritorna custom token in clipboard)
 *  6. Grant / Revoke sudo      (ReasonConfirmDialog toggle in base a hasSudo)
 *
 * Verify email + Delete user restano in detail page (azioni rare, anti fat-finger).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MoreHorizontal,
  Pencil,
  KeyRound,
  Ban,
  CheckCircle2,
  UserCog,
  Shield,
  ShieldOff,
  ExternalLink,
  Copy,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { adminApi, type AdminUserRow } from '@/services/adminApi';

import { ReasonConfirmDialog } from './ReasonConfirmDialog';
import { PermanentDeleteDialog } from './PermanentDeleteDialog';
import { EditUserDrawer } from './EditUserDrawer';

type DialogKind =
  | { kind: 'none' }
  | { kind: 'edit' }
  | { kind: 'reset-password' }
  | { kind: 'disable' }
  | { kind: 'enable' }
  | { kind: 'impersonate' }
  | { kind: 'grant-sudo' }
  | { kind: 'revoke-sudo' }
  | { kind: 'force-delete' };

export interface UserQuickActionsProps {
  row: AdminUserRow;
  /** Uid dell'admin attualmente loggato — per nascondere self-revoke sudo. */
  selfUid?: string;
  onUpdated?: () => void;
}

export function UserQuickActions({ row, selfUid, onUpdated }: UserQuickActionsProps) {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<DialogKind>({ kind: 'none' });

  const isSelf = selfUid && selfUid === row.uid;
  const close = () => setDialog({ kind: 'none' });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Azioni</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel className="text-xs text-muted-foreground font-normal truncate">
            {row.displayName || row.email}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => navigate(`/admin/users/${row.uid}`)}>
            <ExternalLink className="h-3.5 w-3.5 mr-2" />
            Apri dettaglio
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => setDialog({ kind: 'edit' })}>
            <Pencil className="h-3.5 w-3.5 mr-2" />
            Modifica dettagli
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => setDialog({ kind: 'reset-password' })}>
            <KeyRound className="h-3.5 w-3.5 mr-2" />
            Reset password
          </DropdownMenuItem>

          {!isSelf && (
            row.disabled ? (
              <DropdownMenuItem onClick={() => setDialog({ kind: 'enable' })}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-2" />
                Riabilita account
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => setDialog({ kind: 'disable' })}>
                <Ban className="h-3.5 w-3.5 mr-2" />
                Disabilita account
              </DropdownMenuItem>
            )
          )}

          {!isSelf && !row.disabled && (
            <DropdownMenuItem onClick={() => setDialog({ kind: 'impersonate' })}>
              <UserCog className="h-3.5 w-3.5 mr-2" />
              Impersona (read-only)
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {!isSelf && (
            row.hasSudo ? (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDialog({ kind: 'revoke-sudo' })}
              >
                <ShieldOff className="h-3.5 w-3.5 mr-2" />
                Revoca sudo
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => setDialog({ kind: 'grant-sudo' })}>
                <Shield className="h-3.5 w-3.5 mr-2" />
                Grant sudo
              </DropdownMenuItem>
            )
          )}

          {!isSelf && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDialog({ kind: 'force-delete' })}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Elimina account
              </DropdownMenuItem>
            </>
          )}

          {isSelf && (
            <DropdownMenuLabel className="text-xs text-muted-foreground/70 font-normal italic">
              (Azioni su te stesso disabilitate per safety)
            </DropdownMenuLabel>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit drawer */}
      <EditUserDrawer
        open={dialog.kind === 'edit'}
        onOpenChange={(o) => !o && close()}
        uid={dialog.kind === 'edit' ? row.uid : null}
        onSaved={onUpdated}
      />

      {/* Reset password */}
      <ReasonConfirmDialog
        open={dialog.kind === 'reset-password'}
        onOpenChange={(o) => !o && close()}
        title={`Reset password — ${row.email}`}
        description="Firebase invierà un'email all'utente con un link per impostare nuova password. La password attuale resta valida fino al click sul link."
        confirmLabel="Invia link reset"
        onConfirm={async (reason) => {
          const result = await adminApi.users.resetPassword(row.uid, reason);
          toast.success(`Email inviata a ${result.sentTo}`);
          onUpdated?.();
        }}
      />

      {/* Disable */}
      <ReasonConfirmDialog
        open={dialog.kind === 'disable'}
        onOpenChange={(o) => !o && close()}
        title={`Disabilita — ${row.email}`}
        description="L'utente non potrà più fare login finché non viene riabilitato. Le sue sessioni attive verranno invalidate al prossimo refresh token."
        confirmLabel="Disabilita"
        destructive
        onConfirm={async (reason) => {
          await adminApi.users.disable(row.uid, reason);
          toast.success('Account disabilitato');
          onUpdated?.();
        }}
      />

      {/* Enable */}
      <ReasonConfirmDialog
        open={dialog.kind === 'enable'}
        onOpenChange={(o) => !o && close()}
        title={`Riabilita — ${row.email}`}
        description="L'utente potrà fare login nuovamente."
        confirmLabel="Riabilita"
        onConfirm={async (reason) => {
          await adminApi.users.enable(row.uid, reason);
          toast.success('Account riabilitato');
          onUpdated?.();
        }}
      />

      {/* Impersonate */}
      <ReasonConfirmDialog
        open={dialog.kind === 'impersonate'}
        onOpenChange={(o) => !o && close()}
        title={`Impersona — ${row.email}`}
        description="Genererà un token Firebase custom (read-only) che potrai usare per accedere come questo utente da DevTools console. Le mutation saranno bloccate lato server. Audit log registra l'inizio della sessione."
        confirmLabel="Genera token"
        onConfirm={async (reason) => {
          const result = await adminApi.users.impersonate(row.uid, reason);
          // Custom token nel clipboard
          try {
            await navigator.clipboard.writeText(result.customToken);
            toast.success(
              <div>
                Custom token copiato nel clipboard. Apri DevTools console e usa:
                <pre className="text-xs mt-1 bg-muted p-1 rounded">firebase.auth().signInWithCustomToken(token)</pre>
              </div>,
              { duration: 8000 },
            );
          } catch {
            toast.success(
              <div className="flex items-center gap-2">
                Token generato (clipboard fallita)
                <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(result.customToken)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>,
              { duration: 10000 },
            );
          }
        }}
      />

      {/* Grant sudo */}
      <ReasonConfirmDialog
        open={dialog.kind === 'grant-sudo'}
        onOpenChange={(o) => !o && close()}
        title={`Grant sudo — ${row.email}`}
        description="L'utente avrà accesso completo al backoffice (/admin/*). La modifica diventa attiva al prossimo refresh token client-side (logout + login dell'utente)."
        confirmLabel="Grant sudo"
        onConfirm={async (reason) => {
          await adminApi.users.setSudo(row.uid, true);
          // Best-effort: we'd also log the reason but the existing setSudo API
          // doesn't accept it. The audit middleware logs the action anyway.
          toast.success(`Sudo grantato a ${row.email}. Ricordagli di fare logout+login per attivarlo. Reason: ${reason}`);
          onUpdated?.();
        }}
      />

      {/* Revoke sudo */}
      <ReasonConfirmDialog
        open={dialog.kind === 'revoke-sudo'}
        onOpenChange={(o) => !o && close()}
        title={`Revoca sudo — ${row.email}`}
        description="L'utente perderà accesso al backoffice. Il claim viene rimosso dal token al prossimo refresh."
        confirmLabel="Revoca sudo"
        destructive
        onConfirm={async (reason) => {
          await adminApi.users.setSudo(row.uid, false);
          toast.success(`Sudo revocato a ${row.email}. Reason: ${reason}`);
          onUpdated?.();
        }}
      />

      {/* Force delete user account — typing del UID richiesto (anti fat-finger).
          Endpoint BE: POST /v1/admin/users/:uid/delete con body { reason, confirm: uid }.
          Effetti: cancella Firebase Auth + Firestore user doc + memberships.
          IRREVERSIBILE: dati Firestore companies/{cid}/members/{authUid} restano
          (vengono rimossi solo se quella company viene a sua volta cancellata). */}
      <PermanentDeleteDialog
        open={dialog.kind === 'force-delete'}
        onOpenChange={(o) => !o && close()}
        entityKind={`account utente ${row.email}`}
        expectedName={row.uid}
        onConfirm={async (reason) => {
          await adminApi.users.forceDelete(row.uid, reason);
          toast.success(`Account ${row.email} eliminato definitivamente`);
          onUpdated?.();
        }}
      />
    </>
  );
}
